
import { WordSelectionStrategy } from './WordSelectionStrategy';
import { getYearlySequences } from '../wordLibrary';
import { getDayOfYear, normalizeDate } from '../dateUtils';
import { getSequenceIdFromUrl, parseSequenceId } from '../sequenceUtils';
import { sharedSequenceStateManager } from '../../components/game/managers/SharedSequenceStateManager';

export class PracticeWordStrategy implements WordSelectionStrategy {
  private instanceId: string;
  
  constructor() {
    this.instanceId = Date.now().toString() + Math.random().toString(36).substring(2, 15);
    console.log('🎲 PRACTICE STRATEGY CREATED:', {
      instanceId: this.instanceId,
      timestamp: new Date().toISOString(),
      source: 'PracticeWordStrategy constructor'
    });
  }
  
  getStrategyId(): string {
    return `practice-${this.instanceId}`;
  }
  
  getWordSequence(date: Date): string[] {
    try {
      const isPlayAgain = localStorage.getItem('babblegram_practice_mode_play_again') === 'true';
      const forceGameReset = localStorage.getItem('babblegram_practice_mode_force_reset') === 'true';
      const preserveState = localStorage.getItem('babblegram_force_mode_change') === 'false';
      // Also treat a global strategy reset as a signal to force a brand new sequence
      const strategyResetToken = localStorage.getItem('babblegram_strategy_reset');
      const isStrategyReset = !!strategyResetToken;
      const shouldForceNew = isPlayAgain || isStrategyReset;
      
      // Use atomic state manager for shared sequence handling
      const isSharedSequenceActive = sharedSequenceStateManager.isSharedSequenceActive();
      const sharedSequenceId = sharedSequenceStateManager.getSequenceId();
      
      console.log('🎲 PRACTICE MODE - getWordSequence called:', {
        isPlayAgain,
        forceGameReset,
        isSharedSequenceActive,
        sharedSequenceId,
        preserveState,
        isStrategyReset,
        instanceId: this.instanceId,
        timestamp: new Date().toISOString()
      });
      
      console.log('🎲 PRACTICE MODE - Current stored sequence:', localStorage.getItem('babblegram_practice_mode_sequence'));
      
      // Clear flags after checking them
      if (forceGameReset) {
        localStorage.removeItem('babblegram_practice_mode_force_reset');
      }
      
      // Only clear stored sequences if explicitly playing again, global strategy reset, or not preserving state
      if (shouldForceNew || (!preserveState && forceGameReset)) {
        // Clear stored sequences but keep the play again flag until after we use it
        localStorage.removeItem('babblegram_practice_mode_date');
        localStorage.removeItem('babblegram_practice_mode_sequence');
        console.log('🎲 PRACTICE MODE - Cleared stored sequences for new game');
        // Also clear legacy keys for cleanup
        localStorage.removeItem('babblegram_practice_date');
        localStorage.removeItem('babblegram_practice_sequence');
        console.log('🎲 PRACTICE MODE - Cleared stored sequences due to play again, strategy reset, or force reset');
      }
      
      // Priority 1: Check for shared sequence using atomic state manager
      if (isSharedSequenceActive && sharedSequenceId) {
        console.log('🎲 PRACTICE MODE - Shared sequence is active, getting shared sequence');
        
        const sharedSequence = this.getSharedSequence(sharedSequenceId);
        if (sharedSequence) {
          console.log('🎲 PRACTICE MODE - Using shared sequence:', { sequenceId: sharedSequenceId, sequence: sharedSequence });
          this.storeSequence(sharedSequence);
          this.cleanupSharedKeys();
          return sharedSequence;
        }
      }
      
      // Check for existing stored sequence (unless forcing new, prioritizing shared sequence, or force reset without preserving)
      const shouldUseStored = !shouldForceNew && !isSharedSequenceActive && (preserveState || !forceGameReset);
      if (shouldUseStored) {
        const storedSequence = this.getStoredSequence();
        if (storedSequence) {
          console.log('🎲 PRACTICE MODE - Using stored sequence (preserving state):', {
            firstWord: storedSequence[0],
            preserveState,
            instanceId: this.instanceId,
            timestamp: new Date().toISOString()
          });
          return storedSequence;
        }
      }
      
      // Generate new sequence using date-based selection
      const selectedSequence = this.generateDateBasedSequence();
      
      // Store for session persistence
      this.storeSequence(selectedSequence);
      
      // If triggered by Play Again or global strategy reset, clear the flags now so hooks won't re-trigger
      if (isPlayAgain) {
        console.log('🎲 PRACTICE MODE - Generated new sequence due to play again flag; clearing flag now');
        localStorage.removeItem('babblegram_practice_mode_play_again');
      }
      if (isStrategyReset) {
        console.log('🎲 PRACTICE MODE - Clearing global strategy reset token after generating new sequence');
        localStorage.removeItem('babblegram_strategy_reset');
      }
      
      console.log('🎲 PRACTICE MODE - Generated new sequence:', {
        firstWord: selectedSequence[0],
        instanceId: this.instanceId,
        timestamp: new Date().toISOString()
      });
      
      return selectedSequence;
    } catch (error) {
      console.error('🎲 PRACTICE MODE - Error in word selection:', error);
      return this.getFallbackSequence();
    }
  }
  
  private getSharedSequence(sequenceId: string): string[] | null {
    try {
      console.log('🔗 PRACTICE MODE - Getting shared sequence:', { 
        sequenceId,
        currentURL: window.location.href,
        timestamp: new Date().toISOString()
      });
      
      const sharedDate = parseSequenceId(sequenceId);
      if (!sharedDate) {
        console.warn('🎲 PRACTICE MODE - Invalid shared sequence ID:', sequenceId);
        return null;
      }
      
      console.log('🔗 PRACTICE MODE - Parsed date from sequence:', {
        sequenceId,
        parsedDate: sharedDate.toISOString(),
        parsedDateFormatted: sharedDate.toDateString()
      });
      
      // Generate sequence from the shared date
      const sequence = this.generateSequenceFromDate(sharedDate);
      
      // Store both the date and sequence for this session using mode-specific keys
      localStorage.setItem('babblegram_practice_mode_date', sharedDate.toISOString());
      this.storeSequence(sequence);
      
      console.log('🔗 PRACTICE MODE - Successfully generated shared sequence:', {
        sequenceId,
        sequenceLength: sequence.length,
        firstThreeWords: sequence.slice(0, 3),
        storedDate: sharedDate.toISOString(),
        storedSequenceCheck: localStorage.getItem('babblegram_practice_mode_sequence')?.split(',').slice(0, 3)
      });
      
      // Show notification about shared sequence
      const event = new CustomEvent('babblegram_shared_sequence_loaded', {
        detail: { sequenceId, date: sharedDate }
      });
      window.dispatchEvent(event);
      
      return sequence;
    } catch (error) {
      console.error('🎲 PRACTICE MODE - Error loading shared sequence:', error);
      return null;
    }
  }

  private getStoredSequence(): string[] | null {
    try {
      // Use mode-specific key to prevent cross-contamination
      const storedSequenceJson = localStorage.getItem('babblegram_practice_mode_sequence');
      if (storedSequenceJson) {
        const storedSequence = JSON.parse(storedSequenceJson);
        if (Array.isArray(storedSequence) && storedSequence.length >= 4) {
          return storedSequence;
        }
      }
    } catch (error) {
      console.error('🎲 PRACTICE MODE - Error reading stored sequence:', error);
    }
    return null;
  }
  
  private generateDateBasedSequence(): string[] {
    // Generate a random date that's not within the next 30 days
    const randomDate = this.generateRandomDateOutsideNext30Days();
    
    // Store the practice date for session consistency using mode-specific key
    localStorage.setItem('babblegram_practice_mode_date', randomDate.toISOString());
    
    return this.generateSequenceFromDate(randomDate);
  }
  
  private generateSequenceFromDate(date: Date): string[] {
    // Use the same logic as daily mode to select sequence
    const normalizedDate = normalizeDate(date);
    const year = normalizedDate.getFullYear();
    const sequences = getYearlySequences(year);
    
    if (!sequences || !Array.isArray(sequences)) {
      console.error('Failed to get word sequences for practice year:', year);
      return this.getFallbackSequence();
    }
    
    const dayOfYear = getDayOfYear(normalizedDate);
    const sequenceIndex = dayOfYear % sequences.length;
    
    console.log('🎲 PRACTICE MODE - Date-based selection:', {
      practiceDate: date.toISOString(),
      year,
      dayOfYear,
      sequenceIndex,
      instanceId: this.instanceId,
      timestamp: new Date().toISOString()
    });
    
    return sequences[sequenceIndex];
  }
  
  private generateRandomDateOutsideNext30Days(): Date {
    // Always generate a new random date between 2025-2034, excluding next 30 days
    // Add more entropy to ensure truly random generation
    const entropy = Math.random() + Date.now() + Math.random();
    const today = new Date();
    const next30Days = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    // Create a more random seed based on current timestamp and multiple random values
    const randomSeed = Math.floor(entropy * 1000000);
    
    // Random year between 2025-2034 using the seed
    const randomYear = 2025 + Math.floor((Math.sin(randomSeed) * 0.5 + 0.5) * 10);
    
    // Random month (0-11) with better distribution
    const randomMonth = Math.floor((Math.sin(randomSeed * 1.1) * 0.5 + 0.5) * 12);
    
    // Get the number of days in the selected month/year
    const daysInMonth = new Date(randomYear, randomMonth + 1, 0).getDate();
    const randomDay = Math.floor((Math.sin(randomSeed * 1.2) * 0.5 + 0.5) * daysInMonth) + 1;
    
    // Create date from year, month, and day
    const randomDate = new Date(randomYear, randomMonth, randomDay);
    
    console.log('🎲 PRACTICE MODE - Generated random date with enhanced entropy:', {
      randomSeed,
      year: randomYear,
      month: randomMonth,
      day: randomDay,
      daysInMonth,
      finalDate: randomDate.toISOString(),
      entropy: entropy.toString().slice(0, 10),
      timestamp: new Date().toISOString()
    });
    
    // If the random date falls within next 30 days, adjust it
    if (randomDate >= today && randomDate <= next30Days) {
      // Move it to previous year or add 31 days
      if (Math.random() < 0.5) {
        randomDate.setFullYear(randomDate.getFullYear() - 1);
      } else {
        randomDate.setTime(randomDate.getTime() + 31 * 24 * 60 * 60 * 1000);
      }
      
      console.log('🎲 PRACTICE MODE - Adjusted date to avoid next 30 days:', randomDate.toISOString());
    }
    
    return randomDate;
  }
  
  private storeSequence(sequence: string[]): void {
    try {
      console.log('🎲 PRACTICE MODE - Storing sequence for reuse:', {
        sequence,
        instanceId: this.instanceId,
        timestamp: new Date().toISOString()
      });
      
      // Use mode-specific keys only
      localStorage.setItem('babblegram_practice_mode_sequence', JSON.stringify(sequence));
      
      // Only set the practice mode date if it doesn't already exist
      // This preserves the original random date or shared sequence date
      if (!localStorage.getItem('babblegram_practice_mode_date')) {
        const dateUsed = new Date().toISOString();
        localStorage.setItem('babblegram_practice_mode_date', dateUsed);
        console.log('🎲 PRACTICE MODE - Set fallback date since none existed:', dateUsed);
      }
      
      // Clean up any old shared keys to prevent contamination
      this.cleanupSharedKeys();
    } catch (error) {
      console.error('🎲 PRACTICE MODE - Failed to store sequence:', error);
    }
  }

  private cleanupSharedKeys(): void {
    const oldSharedKeys = [
      'babblegram_word_sequence_cache',
      'babblegram_current_sequence',
      'babblegram_stored_sequence'
    ];
    
    oldSharedKeys.forEach(key => {
      localStorage.removeItem(key);
    });
  }
  
  private getFallbackSequence(): string[] {
    const fallbackOptions = [
      ['map', 'camp', 'champ', 'champy'],
      ['tip', 'trip', 'strip', 'stripe'],
      ['cat', 'chat', 'chart', 'chaste'],
      ['dog', 'dogs', 'dopes', 'dopers'],
      ['run', 'runt', 'trunk', 'trunks']
    ];
    const randomIndex = Math.floor(Math.random() * fallbackOptions.length);
    return fallbackOptions[randomIndex];
  }
}
